/**
 * DynamicDatePicker Component
 *
 * Allows users to select dynamic/relative date expressions like "@today", "@today+7d".
 * Used in filter conditions for fields that support dynamic dates.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  DynamicDateConfig,
  DynamicDateUnit,
  DATE_RANGE_PRESETS,
  parseDynamicDateExpression,
  formatDynamicDateExpression,
  resolveDynamicDateForPreview,
  getDynamicDateDisplayLabel,
  findMatchingPreset,
} from '../utils/dynamicColumns';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';

// =============================================================================
// TYPES
// =============================================================================

interface DynamicDatePickerProps {
  /** Current value as dynamic expression (e.g., "@today", "@today+7d") */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Show time-based options like "Now" */
  includeTime?: boolean;
  /** Additional CSS class */
  className?: string;
}

interface DynamicDateRangePickerProps {
  /** Current value as "start to end" (e.g., "@today-7d to @today") */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Additional CSS class */
  className?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

type BaseOption = {
  value: DynamicDateConfig['base'];
  label: string;
  supportsOffset: boolean;
};

const BASE_OPTIONS: BaseOption[] = [
  { value: 'today', label: 'Today', supportsOffset: true },
  { value: 'now', label: 'Now', supportsOffset: true },
  { value: 'start_of_week', label: 'Start of Week', supportsOffset: false },
  { value: 'start_of_month', label: 'Start of Month', supportsOffset: false },
  { value: 'end_of_month', label: 'End of Month', supportsOffset: false },
  { value: 'start_of_last_month', label: 'Start of Last Month', supportsOffset: false },
  { value: 'end_of_last_month', label: 'End of Last Month', supportsOffset: false },
  { value: 'start_of_quarter', label: 'Start of Quarter', supportsOffset: false },
  { value: 'start_of_year', label: 'Start of Year', supportsOffset: false },
];

// Minutes/hours only valid with base 'now' (backend rejects @today±Nh/Nmin);
// business days only with 'today' (whole weekday steps, backend rejects @now±Nbd)
const UNIT_OPTIONS: { value: DynamicDateUnit; label: string; nowOnly?: boolean; todayOnly?: boolean }[] = [
  { value: 'minutes', label: 'Minutes', nowOnly: true },
  { value: 'hours', label: 'Hours', nowOnly: true },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'business_days', label: 'Business Days', todayOnly: true },
];

function unitAllowedForBase(option: (typeof UNIT_OPTIONS)[number], base: DynamicDateConfig['base']): boolean {
  if (option.nowOnly && base !== 'now') return false;
  if (option.todayOnly && base !== 'today') return false;
  return true;
}

// =============================================================================
// SINGLE DATE PICKER
// =============================================================================

/**
 * DynamicDatePicker - Single dynamic date selector
 *
 * UI Layout:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  [Today ▼]   [+][-]  [7]  [Days ▼]                              │
 * │                                                                  │
 * │  Preview: Today + 7 Days → Jan 6, 2026                          │
 * └─────────────────────────────────────────────────────────────────┘
 */
export function DynamicDatePicker({
  value,
  onChange,
  includeTime = false,
  className = '',
}: DynamicDatePickerProps) {
  const disabled = useIsDisabledByPermission();

  // Parse current value into config
  const config = useMemo(() => {
    return parseDynamicDateExpression(value) || { base: 'today' as const };
  }, [value]);

  // Local state for the form
  const [base, setBase] = useState<DynamicDateConfig['base']>(config.base);
  const [offsetDirection, setOffsetDirection] = useState<'+' | '-'>(
    config.offset && config.offset < 0 ? '-' : '+'
  );
  const [offsetAmount, setOffsetAmount] = useState(
    config.offset ? Math.abs(config.offset) : 0
  );
  const [unit, setUnit] = useState<DynamicDateUnit>(config.unit || 'days');

  // Sync local state when value prop changes
  useEffect(() => {
    const parsed = parseDynamicDateExpression(value);
    if (parsed) {
      setBase(parsed.base);
      setOffsetDirection(parsed.offset && parsed.offset < 0 ? '-' : '+');
      setOffsetAmount(parsed.offset ? Math.abs(parsed.offset) : 0);
      setUnit(parsed.unit || 'days');
    }
  }, [value]);

  // Check if current base supports offset
  const currentBaseOption = BASE_OPTIONS.find((o) => o.value === base);
  const supportsOffset = currentBaseOption?.supportsOffset ?? false;

  // Build new expression and notify parent
  const updateValue = (
    newBase: DynamicDateConfig['base'],
    newDirection: '+' | '-',
    newAmount: number,
    newUnit: DynamicDateUnit
  ) => {
    const baseOption = BASE_OPTIONS.find((o) => o.value === newBase);
    const canOffset = baseOption?.supportsOffset ?? false;

    const newConfig: DynamicDateConfig = {
      base: newBase,
      offset: canOffset && newAmount > 0 ? (newDirection === '-' ? -newAmount : newAmount) : undefined,
      unit: canOffset && newAmount > 0 ? newUnit : undefined,
    };

    onChange(formatDynamicDateExpression(newConfig));
  };

  // Handle base change
  const handleBaseChange = (newBase: DynamicDateConfig['base']) => {
    setBase(newBase);
    // Minutes/hours only valid with 'now', business days only with 'today' —
    // coerce to days when the new base can't carry the current unit
    let effectiveUnit = unit;
    const unitOption = UNIT_OPTIONS.find((o) => o.value === unit);
    if (unitOption && !unitAllowedForBase(unitOption, newBase)) {
      effectiveUnit = 'days';
      setUnit('days');
    }
    const baseOption = BASE_OPTIONS.find((o) => o.value === newBase);
    if (!baseOption?.supportsOffset) {
      // Reset offset if new base doesn't support it
      setOffsetAmount(0);
      updateValue(newBase, offsetDirection, 0, effectiveUnit);
    } else {
      updateValue(newBase, offsetDirection, offsetAmount, effectiveUnit);
    }
  };

  // Handle direction toggle
  const handleDirectionToggle = () => {
    const newDirection = offsetDirection === '+' ? '-' : '+';
    setOffsetDirection(newDirection);
    updateValue(base, newDirection, offsetAmount, unit);
  };

  // Handle amount change - only allow integers
  const handleAmountChange = (inputValue: string) => {
    // Remove any non-digit characters
    const digitsOnly = inputValue.replace(/\D/g, '');
    const newAmount = parseInt(digitsOnly, 10) || 0;
    const clamped = Math.max(0, Math.min(365, newAmount));
    setOffsetAmount(clamped);
    updateValue(base, offsetDirection, clamped, unit);
  };

  // Handle unit change
  const handleUnitChange = (newUnit: DynamicDateUnit) => {
    setUnit(newUnit);
    updateValue(base, offsetDirection, offsetAmount, newUnit);
  };

  // Calculate preview date
  const previewDate = useMemo(() => {
    return resolveDynamicDateForPreview(value);
  }, [value]);

  // Filter base options based on includeTime
  const filteredBaseOptions = includeTime
    ? BASE_OPTIONS
    : BASE_OPTIONS.filter((o) => o.value !== 'now');

  return (
    <div className={`space-y-1.5 ${className}`}>
      {/* Main controls row */}
      <div className="flex items-center gap-1.5">
        {/* Base selector - compact width when offset controls are shown */}
        <select
          value={base}
          onChange={(e) => handleBaseChange(e.target.value as DynamicDateConfig['base'])}
          disabled={disabled}
          className={`py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
            supportsOffset ? 'w-[70px]' : ''
          }`}
        >
          {filteredBaseOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Offset controls (only shown if base supports it) */}
        {supportsOffset && (
          <>
            {/* Direction toggle */}
            <button
              type="button"
              onClick={handleDirectionToggle}
              disabled={disabled}
              className={`w-8 h-8 flex items-center justify-center rounded border text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                offsetDirection === '+'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400'
              }`}
              title={offsetDirection === '+' ? 'Adding (click to subtract)' : 'Subtracting (click to add)'}
            >
              {offsetDirection}
            </button>

            {/* Amount input */}
            <input
              type="text"
              inputMode="numeric"
              value={offsetAmount}
              onChange={(e) => handleAmountChange(e.target.value)}
              disabled={disabled}
              className="w-11 px-1 py-1.5 bg-panel border border-gray-300 dark:border-gray-600 rounded text-sm text-body text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />

            {/* Unit selector */}
            <select
              value={unit}
              onChange={(e) => handleUnitChange(e.target.value as DynamicDateUnit)}
              disabled={disabled}
              className="py-1.5 bg-panel border border-gray-300 dark:border-gray-600 rounded text-sm text-body focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {UNIT_OPTIONS.filter((option) => unitAllowedForBase(option, base)).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Preview */}
      <div className="text-xs text-muted flex items-center gap-1">
        <span className="font-medium">{getDynamicDateDisplayLabel(value)}</span>
        <span>→</span>
        <span>
          {previewDate
            ? previewDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                // @now-based expressions carry a time component — show it
                ...(config.base === 'now' ? { hour: 'numeric', minute: '2-digit' } : {}),
              })
            : 'Invalid'}
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// DATE RANGE PICKER (for Between operator)
// =============================================================================

/**
 * DynamicDateRangePicker - Date range selector with presets
 *
 * UI Layout:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  [Last 7 Days              ▼]                                   │
 * │                                                                  │
 * │  Preview: Dec 23, 2025 to Dec 30, 2025                          │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * When "Custom Range" is selected:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  [Custom Range             ▼]                                   │
 * │                                                                  │
 * │  From: [Today ▼] [-] [7] [Days ▼]                               │
 * │  To:   [Today ▼]                                                │
 * │                                                                  │
 * │  Preview: Dec 23, 2025 to Dec 30, 2025                          │
 * └─────────────────────────────────────────────────────────────────┘
 */
export function DynamicDateRangePicker({
  value,
  onChange,
  className = '',
}: DynamicDateRangePickerProps) {
  const disabled = useIsDisabledByPermission();

  // Check if current value matches a preset
  const matchingPreset = useMemo(() => findMatchingPreset(value), [value]);

  // Parse start and end from value
  const [startExpr, endExpr] = useMemo(() => {
    if (!value || !value.includes(' to ')) return ['@today-7d', '@today'];
    const parts = value.split(' to ').map((s) => s.trim());
    return [parts[0] || '@today-7d', parts[1] || '@today'];
  }, [value]);

  // Local state
  const [selectedPreset, setSelectedPreset] = useState<string>(
    matchingPreset ? matchingPreset.label : 'custom'
  );

  // Sync preset selection when value changes externally
  useEffect(() => {
    const preset = findMatchingPreset(value);
    setSelectedPreset(preset ? preset.label : 'custom');
  }, [value]);

  // Handle preset change
  const handlePresetChange = (presetLabel: string) => {
    setSelectedPreset(presetLabel);

    if (presetLabel === 'custom') {
      // Keep current value for custom
      return;
    }

    const preset = DATE_RANGE_PRESETS.find((p) => p.label === presetLabel);
    if (preset) {
      onChange(`${preset.start} to ${preset.end}`);
    }
  };

  // Handle custom start change
  const handleStartChange = (newStart: string) => {
    onChange(`${newStart} to ${endExpr}`);
  };

  // Handle custom end change
  const handleEndChange = (newEnd: string) => {
    onChange(`${startExpr} to ${newEnd}`);
  };

  // Calculate preview dates
  const previewStart = useMemo(() => resolveDynamicDateForPreview(startExpr), [startExpr]);
  const previewEnd = useMemo(() => resolveDynamicDateForPreview(endExpr), [endExpr]);

  const formatPreviewDate = (date: Date | null) => {
    if (!date) return 'Invalid';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Preset dropdown */}
      <select
        value={selectedPreset}
        onChange={(e) => handlePresetChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 bg-panel border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-body focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {DATE_RANGE_PRESETS.map((preset) => (
          <option key={preset.label} value={preset.label}>
            {preset.label}
          </option>
        ))}
        <option value="custom">Custom Range...</option>
      </select>

      {/* Custom range pickers (only shown when custom is selected) */}
      {selectedPreset === 'custom' && (
        <div className="space-y-2 pl-2 border-l-2 border-indigo-300 dark:border-indigo-600">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              From:
            </label>
            <DynamicDatePicker value={startExpr} onChange={handleStartChange} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              To:
            </label>
            <DynamicDatePicker value={endExpr} onChange={handleEndChange} />
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="text-xs text-muted bg-gray-50 dark:bg-gray-700/50 px-2 py-1.5 rounded">
        <span className="font-medium">Preview:</span>{' '}
        {formatPreviewDate(previewStart)} to {formatPreviewDate(previewEnd)}
      </div>
    </div>
  );
}

export default DynamicDatePicker;
